import type { z } from 'zod';
import type { ToolDefinition } from './define.js';
import * as patient from './patient.js';
import * as scheduling from './scheduling.js';
import * as clinical from './clinical.js';
import * as admin from './admin.js';

const all = [
  // patient & context
  patient.searchPatients, patient.getPatient, patient.setPatientContext, patient.clearPatientContext, patient.getPatientContext,
  patient.createRegistrationDraft, patient.updatePatientDemographicsDraft, patient.getPatientSummary, patient.getMedicalHistory,
  patient.getVitals, patient.createRecordEntryDraft, patient.getMyPatientRecord,
  // scheduling
  scheduling.listProviders, scheduling.getAppointments, scheduling.findAvailableSlots, scheduling.createAppointmentDraft,
  scheduling.confirmAppointment, scheduling.cancelAppointmentDraft, scheduling.rescheduleAppointmentDraft,
  scheduling.checkInAppointment, scheduling.markNoShow, scheduling.proposeFollowUp, scheduling.getProviderSchedule,
  // clinical
  clinical.createEncounter, clinical.getEncounters, clinical.updateEncounterDraft, clinical.completeEncounter,
  clinical.getLabResults, clinical.getLabTrends, clinical.getRadiologyReports,
  clinical.getMedicationList, clinical.searchMedicationCatalog, clinical.createPrescriptionDraft, clinical.confirmPrescription,
  clinical.createOrderDraft, clinical.confirmOrder, clinical.getOrders,
  clinical.getClinicalNotes, clinical.createClinicalNoteDraft, clinical.saveClinicalNote, clinical.signClinicalNote,
  clinical.evaluateClinicalDecisionSupport,
  // generic & admin
  admin.getPendingActions, admin.confirmPendingAction, admin.rejectPendingAction, admin.updateMyPreferences,
  admin.provisionTenant, admin.getTenant, admin.updateTenantSettings, admin.createUser, admin.listUsers,
  admin.upsertProviderSchedule, admin.getUsage, admin.createDocumentUpload, admin.listDocuments, admin.getDocumentUrl,
] as const;

export type ToolRegistry = { [K in (typeof all)[number]['name']]: Extract<(typeof all)[number], { name: K }> };
export type ToolName = keyof ToolRegistry;
export type ToolInput<N extends ToolName> = z.input<ToolRegistry[N]['input']>;
export type ToolParsedInput<N extends ToolName> = z.output<ToolRegistry[N]['input']>;
export type ToolOutput<N extends ToolName> = z.output<ToolRegistry[N]['output']>;

export const TOOLS: ToolRegistry = Object.fromEntries(all.map((t) => [t.name, t])) as ToolRegistry;
export const TOOL_NAMES = all.map((t) => t.name) as ToolName[];
export const LLM_TOOLS = all.filter((t) => t.exposeToLlm) as readonly ToolDefinition[];

export function getTool(name: string): ToolDefinition | undefined {
  return (TOOLS as Record<string, ToolDefinition>)[name];
}

/** Implemented by the domain layer; the API and orchestrator only ever call this. */
export interface ToolExecutor {
  execute<N extends ToolName>(name: N, input: ToolInput<N>, ctx: ToolExecutionContext): Promise<ToolOutput<N>>;
}

export interface ToolExecutionContext {
  principal: import('../types/common.js').Principal;
  requestId: string;
  /** Natural-language text that produced this call, retained for audit only when the user approved it. */
  commandText?: string;
  now?: () => Date;
}

export { patient, scheduling, clinical, admin };
